/**
 * Creates and returns a Navbar store with state and methods.
 * @function
 * @returns {NavbarStore} The Navbar store object.
 */
export const createNavbarStore = () => {
    let openItemId = $state(null);
    let openMenuItemId = $state(null);
    let isMenuOpen = $state(false);
    let isDrawerOpen = $state(false);
    let menuDepth = $state(0);
    let direction = $state('down');
    let hoverTimeoutId = $state(null);
    let closeTimeoutId = $state(null);
    let isSearchOpen = $state(false);
    let keepSearchOpen = $state(false);
    let isDrawerFullHeight = $state(false);
    /**
     * Sets the open item and determines the direction of the menu animation.
     * @param {string | null} id - The ID of the item to open.
     */
    const setOpenItem = (id) => {
        const element = document.querySelector(`[data-id="${id}"]`);
        const nextElementId = element?.parentElement?.nextElementSibling?.children[0].getAttribute('data-id');
        const prevElementId = element?.parentElement?.previousElementSibling?.children[0].getAttribute('data-id');
        isSearchOpen = false;
        if (openItemId === nextElementId) {
            direction = "left";
        }
        else if (openItemId === prevElementId) {
            direction = "right";
        }
        else {
            direction = "down";
        }
        openItemId = id;
    };
    /**
     * Opens the search and closes the menu.
     */
    const openSearch = () => {
        isMenuOpen = false;
        isDrawerOpen = false;
        isSearchOpen = true;
    };
    /**
     * Toggles the drawer open state.
     */
    const toggleDrawer = () => {
        isMenuOpen = false;
        isSearchOpen = false;
        isDrawerOpen = !isDrawerOpen;
    };
    /**
     * Opens the drawer.
     */
    const openDrawer = () => {
        isMenuOpen = false;
        isSearchOpen = false;
        isDrawerOpen = true;
    };
    /**
     * Closes the drawer.
     */
    const closeDrawer = () => {
        isDrawerOpen = false;
    };
    /**
     * Triggers keeping the search open for a short duration. Used for focus management.
     */
    const triggerKeepSearchOpen = () => {
        keepSearchOpen = true;
        setTimeout(() => {
            keepSearchOpen = false;
        }, 500);
    };
    /**
     * Closes the search.
     */
    const closeSearch = () => {
        isSearchOpen = false;
    };
    /**
     * Sets the open menu items and updates the menu depth.
     * @param {string[] | null} ids - The IDs of the menu items to open.
     */
    const setOpenMenuItem = (ids) => {
        openMenuItemId = ids;
        menuDepth = ids?.length || 0;
    };
    /**
     * Toggles the menu open state.
     */
    const toggleMenu = () => {
        isDrawerOpen = false;
        isSearchOpen = false;
        isMenuOpen = !isMenuOpen;
    };
    /**
     * Closes the menu and resets related states.
     */
    const closeMenu = () => {
        isMenuOpen = false;
        openItemId = null;
        openMenuItemId = null;
        isSearchOpen = false;
    };
    /**
     * Opens the menu.
     */
    const openMenu = () => {
        isDrawerOpen = false;
        isSearchOpen = false;
        isMenuOpen = true;
    };
    /**
     * Sets the hover timeout ID.
     * @param {number | null} id - The timeout ID to set.
     */
    const setHoverTimeout = (id) => {
        hoverTimeoutId = id;
    };
    /**
     * Sets the close timeout ID.
     * @param {number | null} id - The timeout ID to set.
     */
    const setCloseTimeout = (id) => {
        closeTimeoutId = id;
    };
    const closeAll = () => {
        closeMenu();
        closeSearch();
        closeDrawer();
    };
    /**
     * Sets the drawer full height.
     * @param {boolean} value - The value to set.
     */
    const setDrawerFullHeight = (fullHeight) => {
        isDrawerFullHeight = fullHeight;
    };
    /**
     * Returns the current state of the Navbar store.
     * @returns {NavbarStore} The current state of the Navbar store.
     */
    return {
        get openItemId() { return openItemId; },
        get isMenuOpen() { return isMenuOpen; },
        get isDrawerOpen() { return isDrawerOpen; },
        get isSearchOpen() { return isSearchOpen; },
        get hoverTimeoutId() { return hoverTimeoutId; },
        get closeTimeoutId() { return closeTimeoutId; },
        get direction() { return direction; },
        get openMenuItemId() { return openMenuItemId; },
        get menuDepth() { return menuDepth; },
        get keepSearchOpen() { return keepSearchOpen; },
        get isDrawerFullHeight() { return isDrawerFullHeight; },
        setOpenItem,
        setOpenMenuItem,
        toggleMenu,
        closeMenu,
        openMenu,
        toggleDrawer,
        openDrawer,
        closeDrawer,
        openSearch,
        closeSearch,
        triggerKeepSearchOpen,
        setHoverTimeout,
        setCloseTimeout,
        closeAll,
        setDrawerFullHeight,
    };
};
